import { BackButton } from '../components/BackButton';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  EmptyState,
  ErrorMessage,
} from '../components/LoadingGrid';
import { PlatformChips } from '../components/PlatformChips';
import { Poster } from '../components/Poster';
import { api } from '../lib/api-client';
import { useLang } from '../lib/lang';
import type { Drama } from '../lib/types';

/**
 * Halaman cari. Query & platform disimpan di URL (?q=&platform=) supaya bisa
 * di-share dan tombol back/forward benar. Search di-trigger saat submit,
 * bukan per-ketikan — hemat kuota API (search 60 RPM).
 *
 * CATATAN: endpoint /search butuh SATU platform nyata — nilai `all` yang
 * dipakai beranda ditolak backend. Karena itu default di sini jatuh ke
 * platform pertama yang tersedia, bukan ke config.defaultPlatform.
 */
export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const lang = useLang();

  const platformsQuery = useQuery({
    queryKey: ['platforms'],
    queryFn: () => api.platforms(),
    staleTime: Infinity,
  });

  const fallbackPlatform = platformsQuery.data?.items[0]?.id ?? 'netshort';
  const platform = params.get('platform') ?? fallbackPlatform;
  const q = params.get('q') ?? '';
  const [input, setInput] = useState(q);

  const searchQuery = useQuery({
    queryKey: ['search', platform, q, lang],
    queryFn: () => api.search(platform, q, lang, 1),
    enabled: q.trim().length > 0,
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    next.set('q', input.trim());
    next.set('platform', platform);
    setParams(next, { replace: false });
  };

  const setPlatform = (p: string) => {
    const next = new URLSearchParams(params);
    next.set('platform', p);
    if (q) next.set('q', q);
    setParams(next, { replace: true });
  };

  const clear = () => {
    setInput('');
    const next = new URLSearchParams(params);
    next.delete('q');
    setParams(next, { replace: true });
  };

  const results = searchQuery.data?.items ?? [];

  return (
    <div className="mx-auto min-h-screen max-w-3xl px-4 pb-8 safe-top">
      {/* Header */}
      <header className="flex items-center gap-1 py-2">
        <BackButton />
        <h1 className="font-display text-xl font-bold">Cari</h1>
      </header>

      {/* Kolom pencarian */}
      <form onSubmit={submit}>
        <div className="flex min-h-[48px] items-center gap-3 rounded-2xl border border-brand-500/30 bg-ink-850 px-4">
          <svg className="text-brand-500" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
            <circle cx="11" cy="11" r="7.5" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Cari donghua atau drama…"
            enterKeyHint="search"
            /* text-base = 16px. Di bawah 16px, iOS/WebView memperbesar
               halaman otomatis saat input difokus dan layout jadi meluber. */
            className="min-w-0 flex-1 bg-transparent py-3 text-base text-neutral-100 outline-none placeholder:text-neutral-600"
            autoFocus
          />
          {input.length > 0 && (
            <button
              type="button"
              onClick={clear}
              aria-label="Hapus"
              className="-mr-2 grid h-11 w-11 shrink-0 place-items-center"
            >
              <span className="grid h-5 w-5 place-items-center rounded-full bg-neutral-700">
                <svg className="text-neutral-300" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </span>
            </button>
          )}
        </div>
      </form>

      {/* Filter platform */}
      {platformsQuery.data && (
        <div className="mt-3">
          <PlatformChips
            platforms={platformsQuery.data.items}
            selected={platform}
            onSelect={setPlatform}
          />
        </div>
      )}

      {/* Hasil */}
      <section className="mt-5">
        {q.trim().length === 0 ? (
          <EmptyState message="Ketik kata kunci lalu tekan Enter." />
        ) : searchQuery.isPending ? (
          <div className="flex flex-col gap-2.5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-[98px] animate-pulse rounded-2xl bg-ink-900" />
            ))}
          </div>
        ) : searchQuery.isError ? (
          <ErrorMessage
            title="Pencarian gagal"
            message={searchQuery.error.message}
            onRetry={() => void searchQuery.refetch()}
          />
        ) : results.length === 0 ? (
          <EmptyState message={`Tidak ada hasil untuk "${q}" di ${platform}.`} />
        ) : (
          <>
            <p className="mb-3 text-[11.5px] text-neutral-600">
              {results.length} hasil untuk &ldquo;{q}&rdquo;
            </p>
            <div className="flex flex-col gap-2.5">
              {results.map((d) => (
                <ResultRow key={`${d.platform}-${d.id}`} drama={d} />
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

/** Upstream kadang mengirim "Unknown" — jangan ditampilkan sebagai fakta. */
function clean(v: string | null): string | null {
  if (!v) return null;
  return v.trim().toLowerCase() === 'unknown' ? null : v;
}

/** Baris hasil: poster kecil + judul + metadata, mudah dipindai. */
function ResultRow({ drama }: { drama: Drama }) {
  const meta = [
    drama.platform.toUpperCase(),
    clean(drama.status),
    drama.totalEpisodes ? `${drama.totalEpisodes} ep` : null,
  ].filter(Boolean) as string[];

  return (
    <Link
      to={`/drama/${drama.platform}/${drama.id}`}
      className="flex items-center gap-3 rounded-2xl bg-ink-900 p-2.5 transition active:scale-[0.99]"
    >
      <div className="h-[78px] w-[56px] shrink-0 overflow-hidden rounded-xl bg-ink-800">
        <Poster src={drama.cover} title={drama.title} />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="line-clamp-2 text-sm font-bold leading-tight">
          {drama.title}
        </h3>
        <p className="mt-1.5 text-[11px] text-neutral-500">
          {meta.join(' · ')}
        </p>
      </div>
      <svg className="text-neutral-500" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  );
}
